/**
 * The data panel's **Holdings** tab: what the Top 15 funds hold of one symbol,
 * from their 13F filings (`docs/plans/holdings-13f.md`; the approved design is
 * `docs/mockups/holdings-13f.html` §3).
 *
 * Top to bottom: a box of **Total (latest filed) · the base quarter's change ·
 * the newest quarter's change**, the **quarter by quarter** adds and cuts, and
 * the **holders**. Three rules a reader relies on, each kept here:
 *
 * - **Missing is never selling.** The newest quarter fills in fund by fund
 *   through its 45-day window; until a fund files, its cell is `·`, never $0.
 * - **$ is the trade, not the price move** — Δ shares × the quarter's close, so
 *   a stock that rose is not a buyer. The arithmetic is `lib/holdings-view.ts`.
 * - **Two percentages, labelled**: of the company (`pctOut`) on the bar, of the
 *   fund's own book in its own column. A share of the fifteen was tried and
 *   dropped: BlackRock is ~77% of it for almost any stock.
 *
 * **⤢ Expand** puts the expanded page over the whole window, from a panel of
 * any width (owner, 2026-10-13). It is an overlay rather than ⧉'s window
 * because a popup can be refused, and a window still has to be widened past
 * `EXPANDED_WIDTH` by hand. It is rendered in place, not portalled: the theme's
 * `--qf-*` variables live on the shell's root, not on `<body>`.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactElement, type RefObject } from "react";
import { HoldingsExpanded } from "./HoldingsExpanded";
import { FundOverlay } from "./HoldingsFund";
import type { Holding, HoldingsSymbol } from "../api/holdings";
import {
  dueDate, earlierHolders, fundColor, holderQuarters, optionsHeavy, optionsNote, pct, quarterCell, quarterLabel,
  shortDate, summary, useHoldings, usd, type QuarterCell,
} from "../lib/holdings-view";

interface Props {
  symbol: string;
  compact?: boolean;
}

/** Width at which the tab becomes the expanded page — the detached window, or a wide dock. */
export const EXPANDED_WIDTH = 900;

/** Opens a fund's page: one of the fifteen by id (`berkshire`), anyone else by CIK. */
type OnFund = (fund: string) => void;

/**
 * What is open over the window: a fund's page, or a stock's Holdings page
 * reached from a tile of a fund's heatmap. One at a time — a click swaps the
 * page rather than stacking another, and ✕ or Esc goes back to the panel.
 */
export type HoldingsPage = { fund: string } | { symbol: string; from?: { fund: string; name: string } };

export function PageOverlay({ page, onPage, onClose }: {
  page: HoldingsPage; onPage: (p: HoldingsPage) => void; onClose: () => void;
}): ReactElement {
  const onFund = (fund: string): void => onPage({ fund });
  if ("fund" in page) {
    return <FundOverlay fund={page.fund} onClose={onClose} onFund={onFund} onSymbol={(symbol, from) => onPage({ symbol, from })} />;
  }
  return <SymbolOverlay symbol={page.symbol} from={page.from} onClose={onClose} onFund={onFund} />;
}

/** A stock's Holdings page over the window — the panel's own layout, at the overlay's width. */
function SymbolOverlay({ symbol, from, onClose, onFund }: {
  symbol: string; from?: { fund: string; name: string }; onClose: () => void; onFund: OnFund;
}): ReactElement {
  const load = useHoldings(symbol);
  const [host, width] = useWidth();
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div style={{ ...OVERLAY, zIndex: 1600 }} role="dialog" aria-modal="true" aria-label={`${symbol} holdings`} data-testid="holdings-symbol">
      <div style={OVERLAY_BAR}>
        <span style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
          {from && (
            <button type="button" onClick={() => onFund(from.fund)} style={{ ...EXPAND, ...NM, maxWidth: "40vw" }}
                    title={`Back to ${from.name}'s fund page`}>
              ← {from.name}
            </button>
          )}
          <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{symbol} · Holdings — Top 15 funds</span>
        </span>
        <button type="button" onClick={onClose} style={EXPAND} aria-label="Close">✕</button>
      </div>
      <div ref={host} style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{body(load, width, undefined, onFund)}</div>
    </div>
  );
}

export function HoldingsView({ symbol }: Props): ReactElement {
  const load = useHoldings(symbol);
  const [host, width] = useWidth();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<HoldingsPage | null>(null);
  const setFund = (fund: string): void => setPage({ fund });
  return (
    <div ref={host} style={{ height: "100%" }}>
      {body(load, width, () => setOpen(true), setFund)}
      {open && load.state === "ready" && load.data.state !== "never" && (
        <Overlay h={load.data} onClose={() => setOpen(false)} onFund={setFund} />
      )}
      {page && <PageOverlay page={page} onPage={setPage} onClose={() => setPage(null)} />}
    </div>
  );
}

/** The tab at a width. No `onExpand` when it is already over the window. */
function body(load: ReturnType<typeof useHoldings>, width: number, onExpand: (() => void) | undefined, onFund: OnFund): ReactElement {
  if (load.state === "loading") return <Note text="loading…" />;
  if (load.state === "none") {
    return <Note text="Holdings are not available here yet — the 13F data has not reached this server." />;
  }
  const h = load.data;
  if (width >= EXPANDED_WIDTH && h.state !== "never") {
    return <HoldingsExpanded h={h} summary={<SummaryBox h={h} />}
                             holders={<><Holders h={h} onFund={onFund} /><Others h={h} onFund={onFund} /></>} />;
  }
  return (
    <div style={SCROLL} data-testid="holdings-view">
      <div style={ASOF_ROW}>
        <div style={ASOF}>
          Latest filed: quarter ended {h.window.base} · {quarterLabel(h.window.qn)} due {shortDate(dueDate(h.window.qn))}
          {" "}· shares only
        </div>
        {h.state !== "never" && onExpand && (
          <button type="button" onClick={onExpand} style={EXPAND} title="Show the expanded page over the whole window">
            ⤢ Expand
          </button>
        )}
      </div>

      {h.state === "never" ? (
        <>
          <Card title={`None of the 15 funds holds ${h.symbol}`}
                text="No shares, no options and no exits in their filings for the year held. In the screener this is jpTop15HeldCount = 0." />
          <Others h={h} onFund={onFund} />
        </>
      ) : (
        <>
          {h.state === "exited" && (
            <Card title={`No current holders of ${h.symbol}`}
                  text={`${h.exited.length} fund${h.exited.length === 1 ? "" : "s"} sold out — listed below.`} />
          )}
          {h.state === "options" && (
            <Card title={`No stock holders of ${h.symbol}`}
                  text="Funds hold only calls or puts on it; listed below, and never added to $ held." />
          )}
          <SummaryBox h={h} />
          <Holders h={h} onFund={onFund} />
          <Others h={h} onFund={onFund} />
          <Options h={h} onFund={onFund} />
        </>
      )}
    </div>
  );
}

/** The expanded page over the whole window; ⤡ Collapse, ✕ or Esc closes it. */
function Overlay({ h, onClose, onFund }: { h: HoldingsSymbol; onClose: () => void; onFund: OnFund }): ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div style={OVERLAY} role="dialog" aria-modal="true" aria-label={`${h.symbol} holdings`}>
      <div style={OVERLAY_BAR}>
        <span style={{ fontWeight: 600 }}>{h.symbol} · Holdings — Top 15 funds</span>
        <span style={{ display: "flex", gap: 6 }}>
          <button type="button" onClick={onClose} style={EXPAND}>⤡ Collapse</button>
          <button type="button" onClick={onClose} style={EXPAND} aria-label="Close">✕</button>
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <HoldingsExpanded h={h} summary={<SummaryBox h={h} />}
                          holders={<><Holders h={h} onFund={onFund} /><Others h={h} onFund={onFund} /></>} />
      </div>
    </div>
  );
}

/** The host's width, kept current. 0 until measured — and in jsdom, which has no ResizeObserver. */
function useWidth(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// ── the box: Total · base change · newest change ───────────────────────────
function SummaryBox({ h }: { h: HoldingsSymbol }): ReactElement {
  const s = summary(h);
  const qn = quarterLabel(h.window.qn), base = quarterLabel(h.window.base);
  const pending = <span style={MUTED}>·</span>;
  const cells: [string, ReactElement, ReactElement, ReactElement][] = [
    ["$ held", <>{usd(s.heldValue)} <span style={MUTED}>{base}</span></>,
     <Signed v={s.baseNet} suffix=" traded" />, pending],
    ["funds", <>{s.holders} of 15</>,
     <><span style={POS}>+{s.baseIn}</span> <span style={NEG}>−{s.baseOut}</span></>, pending],
    ["% of " + h.symbol, <>{pct(s.sharesOutPct)}</>, <span style={MUTED}>—</span>, pending],
  ];
  return (
    <div style={BOX}>
      <span style={HEAD} />
      <span style={HEAD}>Total · latest filed</span>
      <span style={HEAD}>{base} change</span>
      <span style={{ ...HEAD, color: "var(--qf-warn)" }}>{qn} change</span>
      {cells.map(([k, a, b, c]) => (
        <span key={k} style={{ display: "contents" }}>
          <span style={KEY}>{k}</span><span style={VAL}>{a}</span><span style={VAL}>{b}</span><span style={VAL}>{c}</span>
        </span>
      ))}
      <span style={FOOT}>
        {qn}: {h.window.filedFunds1Q} of {h.window.funds} filed · due {shortDate(dueDate(h.window.qn))} · each fund's {qn} column
        fills in as it files
      </span>
    </div>
  );
}

// ── the holders, a column per quarter ───────────────────────────────────────
/**
 * One row per fund, its year across: the quarter-by-quarter list this replaced
 * made a reader match coloured bars to names (owner, 2026-10-12); a row that
 * reads Q4 → Q3 says the same once per fund.
 */
function Holders({ h, onFund }: { h: HoldingsSymbol; onFund?: OnFund }): ReactElement | null {
  const earlier = earlierHolders(h);
  if (!h.holders.length && !h.exited.length && !earlier.length) return null;
  const max = Math.max(1, ...h.holders.map((x) => x.value));
  const qs = holderQuarters(h);
  const cols = { ...HROW, gridTemplateColumns: `150px minmax(90px, 1fr) 52px repeat(${qs.length}, 74px)` };
  return (
    <div>
      <div style={{ ...cols, ...SUB_ROW }}>
        <span>Holder</span>
        <span>$ held · % of {h.symbol}</span>
        <span style={{ textAlign: "right" }} title="share of that fund's own portfolio">of book</span>
        {qs.map((q) => (
          <span key={q} style={{ textAlign: "right", whiteSpace: "nowrap", ...(q === h.window.qn ? { color: "var(--qf-warn)" } : {}) }}>
            {quarterLabel(q)}<br /><span style={{ fontSize: "0.8em" }}>$ · shares</span>
          </span>
        ))}
      </div>
      {h.holders.map((x) => (
        <div key={x.fund} style={cols}>
          <Name x={x} onFund={onFund} />
          <Bar x={x} h={h} max={max} />
          <OfBook x={x} title={`${h.symbol} is this share of ${x.fundName}'s portfolio`} />
          {qs.map((q) => <Cell key={q} c={quarterCell(h, x.fund, q)} due={h.window.qn} />)}
        </div>
      ))}
      {h.exited.map((x) => (
        <div key={x.fund} style={cols}>
          <Name x={x} onFund={onFund} />
          <span style={{ ...TRACK, border: "1px dashed var(--qf-negative)", background: "transparent" }} />
          <span style={{ ...D, ...MUTED }}>0%</span>
          {qs.map((q) => <Cell key={q} c={quarterCell(h, x.fund, q)} due={h.window.qn} />)}
        </div>
      ))}
      {earlier.map((fund) => {
        const f = h.funds[fund];
        return (
          <div key={fund} style={{ ...cols, opacity: 0.75 }}>
            <span style={NM} title="held earlier in the year">
              <i style={{ ...DOT, background: fundColor(f?.order) }} />{f?.name ?? fund}
            </span>
            <span style={{ ...MUTED, fontSize: 12 }}>held earlier in the year</span>
            <span style={{ ...D, ...MUTED }}>—</span>
            {qs.map((q) => <Cell key={q} c={quarterCell(h, fund, q)} due={h.window.qn} />)}
          </div>
        );
      })}
    </div>
  );
}

function Bar({ x, h, max }: { x: Holding; h: HoldingsSymbol; max: number }): ReactElement {
  const w = (100 * x.value) / max;
  const label = `${usd(x.value)}${x.pctOut !== null ? ` · ${pct(x.pctOut)}` : ""}`;
  return (
    <span style={TRACK} title={`${usd(x.value)} held${x.pctOut !== null ? ` = ${pct(x.pctOut)} of ${h.symbol}` : ""}`}>
      <span style={{ ...FILL, width: `${Math.max(1.5, w)}%`, background: statusColor(x.status) }} />
      {w > 60
        ? <span style={{ ...W, right: `calc(${100 - w}% + 4px)`, color: "var(--qf-background)", fontWeight: 600 }}>{label}</span>
        : <span style={{ ...W, left: `calc(${Math.max(1.5, w)}% + 4px)` }}>{label}</span>}
    </span>
  );
}

function Cell({ c, due }: { c: QuarterCell; due: string }): ReactElement {
  if (c.kind === "pending") return <span style={{ ...D, ...MUTED }} title={`not filed yet — due ${shortDate(dueDate(due))}`}>·</span>;
  if (c.kind === "held") return <span style={{ ...D, ...MUTED }} title="held, no trade">—</span>;
  if (c.kind === "none") return <span style={D} />;
  return (
    <span style={D}>
      <span style={c.trade >= 0 ? POS : NEG}>{usd(c.trade, true)}</span>
      <br />
      {c.status === "NEW" || c.status === "EXIT"
        ? <span style={TAG(c.status)}>{c.status}</span>
        : <span style={{ fontSize: "0.8em" }}>{c.shares}</span>}
    </span>
  );
}

function Options({ h, onFund }: { h: HoldingsSymbol; onFund?: OnFund }): ReactElement | null {
  const rows = [...h.holders, ...h.optionsOnly].filter((x, i, a) => (x.calls > 0 || x.puts > 0) && a.findIndex((y) => y.fund === x.fund) === i);
  if (!rows.length) return null;
  return (
    <div>
      <div style={SUB}><span>Options, reported value — never in $ held</span><span>calls · puts</span></div>
      {rows.map((x) => (
        <div key={x.fund} style={{ ...HROW, gridTemplateColumns: "1fr 84px 84px" }}>
          <Name x={x} onFund={onFund} />
          <span style={{ ...D, ...POS }}>{x.calls ? usd(x.calls) : "·"}</span>
          <span style={{ ...D, ...NEG }}>{x.puts ? usd(x.puts) : "·"}</span>
        </div>
      ))}
    </div>
  );
}

// ── everyone else: every 13F filer but the fifteen ─────────────────────────
/**
 * The mock's "Other holders vs Top 15" (§3): the two groups' totals side by
 * side, then the largest other holders and the largest exits, each filer at
 * its latest filed quarter. Over every 13F filer, so the two rows together are
 * the stock's whole reported institutional ownership. A name opens its fund page.
 */
export function Others({ h, onFund }: { h: HoldingsSymbol; onFund?: OnFund }): ReactElement | null {
  const o = h.others;
  if (!o || (!o.holders && !o.exits.length)) return null;
  const max = Math.max(1, ...o.rows.map((x) => x.value), ...h.holders.map((x) => x.value));
  const cols = { ...HROW, gridTemplateColumns: "150px minmax(90px, 1fr) 52px 84px" };
  const right: CSSProperties = { ...VAL, textAlign: "right" };
  const last: CSSProperties = { ...right, borderBottom: 0 };
  return (
    <div data-testid="holdings-others">
      <div style={SUB}><span>Other holders vs Top 15 · every 13F filer, each at its latest filing</span></div>
      <div style={{ ...BOX, gridTemplateColumns: "auto 1fr 1fr 1fr", marginBottom: 6 }}>
        <span style={HEAD} />
        <span style={{ ...HEAD, textAlign: "right" }}>holders</span>
        <span style={{ ...HEAD, textAlign: "right" }}>$ held</span>
        <span style={{ ...HEAD, textAlign: "right" }}>% of {h.symbol}</span>
        <span style={KEY}>Top 15</span>
        <span style={right}>{o.top15Holders}</span>
        <span style={right}>{usd(o.top15Value)}</span>
        <span style={right}>{pct(o.top15PctOut)}</span>
        <span style={{ ...KEY, borderBottom: 0 }}>Others</span>
        <span style={last}>{o.holders.toLocaleString()}</span>
        <span style={last}>{usd(o.value)}</span>
        <span style={last}>{pct(o.pctOut)}</span>
      </div>
      <div style={{ ...cols, ...SUB_ROW }}>
        <span>Largest {o.rows.length} of {o.holders.toLocaleString()}</span>
        <span>$ held · % of {h.symbol}</span>
        <span style={{ textAlign: "right" }} title="share of that filer's own portfolio">of book</span>
        <span style={{ textAlign: "right" }}>latest change</span>
      </div>
      {o.rows.map((x) => (
        <div key={x.fund} style={cols}>
          <Name x={x} onFund={onFund} other />
          <Bar x={x} h={h} max={max} />
          <OfBook x={x} title={`${h.symbol} is this share of ${x.fundName}'s stock`} />
          <Change x={x} />
        </div>
      ))}
      {o.exits.length > 0 && (
        <>
          <div style={{ ...SUB_ROW, marginTop: 4 }}>Largest exits</div>
          {o.exits.map((x) => (
            <div key={x.fund} style={cols}>
              <Name x={x} onFund={onFund} other />
              <span style={{ ...TRACK, border: "1px dashed var(--qf-negative)", background: "transparent" }} />
              <span style={{ ...D, ...MUTED }}>0%</span>
              <Change x={x} />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/** "% of book", marked ⚠ for a filer whose 13F is mostly options. */
export function OfBook({ x, title }: { x: Holding; title: string }): ReactElement {
  if (optionsHeavy(x.stockShare)) {
    return (
      <span style={{ ...D, color: "var(--qf-warn)" }} title={optionsNote(x.stockShare as number)} data-testid="options-heavy">
        {pct(x.pctBook)} ⚠
      </span>
    );
  }
  return <span style={{ ...D, color: "var(--qf-text-secondary)" }} title={title}>{pct(x.pctBook)}</span>;
}

/** The filer's trade in its latest filed quarter: NEW / EXIT, else the signed $; `—` held. */
function Change({ x }: { x: Holding }): ReactElement {
  const q = <span style={{ fontSize: "0.8em", ...MUTED }}>{quarterLabel(x.period)}</span>;
  if (x.status === "NEW" || x.status === "EXIT") {
    return (
      <span style={D}>
        <span style={x.trade !== null && x.trade < 0 ? NEG : POS}>{usd(x.trade, true)}</span>
        <br /><span style={TAG(x.status)}>{x.status}</span> {q}
      </span>
    );
  }
  if (x.trade === null || x.status === "HOLD" || x.status === null) {
    return <span style={{ ...D, ...MUTED }} title="held, no trade">— {q}</span>;
  }
  return <span style={D}><span style={x.trade >= 0 ? POS : NEG}>{usd(x.trade, true)}</span><br />{q}</span>;
}

// ── small parts ────────────────────────────────────────────────────────────
function Name({ x, onFund, other }: { x: Holding; onFund?: OnFund; other?: boolean }): ReactElement {
  const dot = other
    ? <i style={{ ...DOT, border: "1px solid var(--qf-text-muted)" }} />
    : <i style={{ ...DOT, background: fundColor(x.fundOrder) }} />;
  if (!onFund) return <span style={NM} title={`${x.fundName} · ${x.manager}`}>{dot}{x.fundName}</span>;
  return (
    <button type="button" style={{ ...NM, ...LINK }} title={`${x.fundName} · ${x.manager} — open its fund page`}
            onClick={() => onFund(x.fund)}>
      {dot}{x.fundName}
    </button>
  );
}

function Signed({ v, suffix = "" }: { v: number; suffix?: string }): ReactElement {
  return <><span style={v >= 0 ? POS : NEG}>{usd(v, true)}</span><span style={MUTED}>{suffix}</span></>;
}

function Card({ title, text }: { title: string; text: string }): ReactElement {
  return (
    <div style={CARD}>
      <div style={{ fontWeight: 600 }}>{title}</div>
      <div style={{ ...MUTED, lineHeight: 1.5 }}>{text}</div>
    </div>
  );
}

function Note({ text }: { text: string }): ReactElement {
  return <div style={{ ...SCROLL, ...MUTED }}>{text}</div>;
}

function statusColor(s: Holding["status"]): string {
  return s === "TRIM" || s === "EXIT" ? "var(--qf-negative)" : s === "NEW" || s === "ADD" ? "var(--qf-positive)" : "var(--qf-accent)";
}

// ── styles (theme tokens only, apart from the fund palette) ────────────────
const MONO = "ui-monospace, 'IBM Plex Mono', Consolas, monospace";
const SCROLL: CSSProperties = { padding: "8px 10px", display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", height: "100%", color: "var(--qf-text)", fontSize: 14 };
const ASOF: CSSProperties = { fontFamily: MONO, fontSize: 12, color: "var(--qf-text-muted)" };
const ASOF_ROW: CSSProperties = { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8 };
const EXPAND: CSSProperties = {
  flexShrink: 0, padding: "2px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
  background: "transparent", color: "var(--qf-text)", border: "1px solid var(--qf-border)", borderRadius: 4,
};
const OVERLAY: CSSProperties = {
  position: "fixed", inset: 0, zIndex: 1500, display: "flex", flexDirection: "column",
  background: "var(--qf-background)", color: "var(--qf-text)", fontSize: 14,
};
const OVERLAY_BAR: CSSProperties = {
  flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "8px 12px", borderBottom: "1px solid var(--qf-border)",
};
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
const POS: CSSProperties = { color: "var(--qf-positive)" };
const NEG: CSSProperties = { color: "var(--qf-negative)" };
const BOX: CSSProperties = { flexShrink: 0, display: "grid", gridTemplateColumns: "auto 1.1fr 1fr .8fr", border: "1px solid var(--qf-border)", borderRadius: 5, overflow: "hidden" };
const HEAD: CSSProperties = { padding: "4px 7px", fontFamily: MONO, fontSize: 11, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--qf-text-muted)", background: "var(--qf-surface-alt)", borderBottom: "1px solid var(--qf-border)" };
const KEY: CSSProperties = { padding: "4px 7px", fontFamily: MONO, fontSize: 12, color: "var(--qf-text-muted)", borderBottom: "1px solid var(--qf-border)" };
const VAL: CSSProperties = { padding: "4px 7px", fontFamily: MONO, fontVariantNumeric: "tabular-nums", borderBottom: "1px solid var(--qf-border)" };
const FOOT: CSSProperties = { gridColumn: "1 / -1", padding: "4px 7px", fontFamily: MONO, fontSize: 12, color: "var(--qf-warn)" };
const SUB: CSSProperties = { display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5, color: "var(--qf-text-muted)", marginBottom: 4 };
const SUB_ROW: CSSProperties = { fontSize: 12.5, color: "var(--qf-text-muted)", padding: 0 };
const DOT: CSSProperties = { display: "inline-block", width: 9, height: 9, borderRadius: 2, marginRight: 5 };
const HROW: CSSProperties = { display: "grid", gridTemplateColumns: "150px 1fr 56px 84px 48px", alignItems: "center", gap: 6, fontSize: 13, padding: "2px 0" };
const NM: CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const LINK: CSSProperties = {
  padding: 0, border: 0, background: "transparent", color: "inherit", font: "inherit", textAlign: "left", cursor: "pointer",
  textDecoration: "underline dotted", textUnderlineOffset: 3,
};
const TRACK: CSSProperties = { height: 16, background: "var(--qf-row-alt)", borderRadius: 2, position: "relative" };
const FILL: CSSProperties = { position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 2 };
const W: CSSProperties = { position: "absolute", top: 0, fontFamily: MONO, fontSize: 11, color: "var(--qf-text-secondary)", whiteSpace: "nowrap" };
const D: CSSProperties = { fontFamily: MONO, fontSize: 12, textAlign: "right", whiteSpace: "nowrap", lineHeight: 1.15 };
const CARD: CSSProperties = { border: "1px dashed var(--qf-border)", borderRadius: 5, padding: "9px 11px", display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 };
const TAG = (s: "NEW" | "EXIT" | string): CSSProperties => ({
  marginLeft: 3, fontFamily: MONO, fontSize: 10.5, fontWeight: 600, padding: "0 3px", borderRadius: 3,
  color: s === "EXIT" ? "var(--qf-negative)" : "var(--qf-positive)", border: `1px solid ${s === "EXIT" ? "var(--qf-negative)" : "var(--qf-positive)"}`,
});
